/**
 * Reloads the app when a newer build has been deployed, so nobody keeps working on a stale bundle
 * (old lazy chunks disappear from the server and screens start failing with generic errors).
 *
 * - Every few minutes, and whenever the tab becomes visible again, fetch /version.json (never cached)
 *   and compare its main bundle name with the one this page loaded. Different → reload.
 * - If a lazy chunk fails to load (ChunkLoadError), reload once (guarded with sessionStorage).
 */
const CHECK_INTERVAL_MS = 3 * 60 * 1000;
const RELOAD_GUARD_KEY = "wapp_chunk_reload_at";

const currentMainBundle = (): string | null => {
	const script = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))
		.map((element) => element.src)
		.find((src) => /\/static\/js\/main\.[a-z0-9]+\.js$/i.test(src));
	if (!script) {
		return null;
	}
	return script.substring(script.lastIndexOf("/") + 1);
};

const reloadOnce = (reason: string) => {
	try {
		const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
		if (Date.now() - last < 60 * 1000) {
			return; // already reloaded very recently; avoid a loop
		}
		sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
	} catch (_) { /* storage unavailable: still reload */ }
	console.info("WAPP: recargando por " + reason);
	window.location.reload();
};

const checkVersion = async () => {
	const mine = currentMainBundle();
	if (!mine) {
		return;
	}
	try {
		const response = await fetch("/version.json?_=" + Date.now(), { cache: "no-store" });
		if (!response.ok) {
			return;
		}
		const data = await response.json();
		if (data && typeof data.main === "string" && data.main !== mine) {
			reloadOnce("nueva versión publicada (" + data.main + ")");
		}
	} catch (_) {
		// offline or transient error: try again on the next tick
	}
};

export const startVersionWatcher = () => {
	if (process.env.NODE_ENV !== "production") {
		return;
	}
	window.setInterval(checkVersion, CHECK_INTERVAL_MS);
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") {
			checkVersion();
		}
	});
	window.addEventListener("error", (event) => {
		const message = String((event && (event.message || (event.error && event.error.message))) || "");
		if (/ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk/i.test(message)) {
			reloadOnce("chunk faltante tras un despliegue");
		}
	});
	window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
		const reason: any = event && event.reason;
		const message = String((reason && (reason.message || reason.name)) || reason || "");
		if (/ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk/i.test(message)) {
			reloadOnce("chunk faltante tras un despliegue");
		}
	});
};
